import { getAccessToken, removeAccessToken, saveAccessToken } from '@/entities/user/lib/token.utils';
import { refreshAccessToken } from '@/features/auth/lib/auth.services';
import { ERROR_CODES } from '@/shared/constants/error.constants';
import { captureSentryError } from '@/shared/lib/utils/sentry.utils';
import { ListResponse } from '@/shared/types/response.types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { replace } from 'react-router-dom';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  timeoutErrorMessage: '요청 시간이 초과되었습니다. 다시 시도해주세요.',
  withCredentials: true // 쿠키 통신을 위해 필요
});

export const publicApi = api.create();
export const authApi = api.create();

authApi.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
};

const handleTokenRefresh = async (originalRequest: any): Promise<any> => {
  originalRequest._retry = true;

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return authApi(originalRequest);
    });
  }

  isRefreshing = true;

  try {
    const { data } = await refreshAccessToken();
    const { accessToken: newAccessToken } = data.body;
    if (!newAccessToken) throw new Error('토큰 갱신 실패');

    saveAccessToken(newAccessToken);
    processQueue(null, newAccessToken);
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    authApi.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

    return authApi(originalRequest);
  } catch (error) {
    processQueue(error, null);
    removeAccessToken();
    replace('/login');
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
};

authApi.interceptors.response.use(
  async (response: AxiosResponse<ListResponse<[]>>) => {
    const originalRequest = response.config as any;
    const responseData = response?.data;
    const isTokenExpired = responseData?.resultCode === ERROR_CODES.TOKEN_EXPIRED;

    if (isTokenExpired && !originalRequest._retry) {
      return handleTokenRefresh(originalRequest);
    }

    return response;
  },
  async (error: AxiosError<ListResponse<[]>>) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest?._retry) {
      return handleTokenRefresh(originalRequest);
    }

    const networkError = new AxiosError(
      '네트워크 에러',
      error.code,
      error.config,
      error.response?.request,
      error.response
    );

    captureSentryError(networkError, {
      tags: {
        feature: 'api',
        source: 'authApi_interceptor',
        status: String(error.response?.status ?? 'unknown')
      },
      context: {
        url: error.config?.url,
        method: error.config?.method
      }
    });

    return Promise.reject(networkError);
  }
);
